import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Close } from '../private/icons/Close';
import { IconButton } from '../button/IconButton';
import { Link } from '../link/Link';
import { Portal } from '../private/components/portal/Portal';
import { Drag } from '../private/components/gestures/Drag';
import { TransitionFade } from '../private/components/fade/TransitionFade';
import { Typography } from '../typography/Typography';
import useControlled from '../private/hooks/useControlled';
import HTMLElementType from '../private/utils/HTMLElementType';

/**
 * A toast notification component that displays brief messages to users.
 *
 * The `Toast` component provides a non-intrusive way to show feedback, alerts, or status updates.
 * It appears at the bottom of the screen and can auto-dismiss after a specified duration.
 * The component supports customization with decorations (icons or images), action links,
 * and various interaction states including pause on hover/focus.
 *
 * @component
 * @param {Object} props - The component props
 * @param {HTMLElement|Function} [props.anchorEl=document.body] - The HTML element or function
 * that returns an element to anchor the toast to
 * @param {React.Node} [props.decoration] - Decoration component or element to display (icon or image)
 * @param {(5000|7000|9000)} [props.duration=5000] - Duration in milliseconds before auto-dismiss
 * @param {React.Node} [props.link] - Link component to display as an action button
 * @param {string} props.message - The message text to display (required)
 * @param {Function} [props.onClose] - Callback function called when toast is closed
 * @param {boolean} [props.open=false] - Controls toast visibility
 * @param {Object} [props.ref] - React ref forwarded to the root element
 *
 * @returns {React.Element|null} The rendered Toast component or null if not open
 *
 * @example
 * ```jsx
 * import { Toast } from '@one-thd/sui-atomic-components';
 * import { CheckIcon } from './icons';
 *
 * <Toast
 *   decoration={<CheckIcon />}
 *   message="Settings saved successfully"
 *   open={isOpen}
 *   onClose={() => setIsOpen(false)}
 * />
 * ```
 */
const Toast = React.forwardRef((props, ref) => {
  const {
    anchorEl = document.body,
    decoration: decorationProp,
    disablePortal = false,
    duration = 5000,
    link: linkProp,
    message: messageProp,
    open: openProp = false,
    onClose,
    onKeyDown,
    ...restProps
  } = props;

  const containerRef = React.useRef(null);
  const squarePosition = React.useRef({ x: 0, y: 0 });
  const timeoutRef = React.useRef(null);
  const closeButtonRef = React.useRef(null);
  const linkRef = React.useRef(null);
  const triggerEl = typeof document !== 'undefined' ? document?.activeElement : null;

  const [open, setOpen] = useControlled({
    controlled: openProp,
    defaultValue: false
  });

  const [exited, setExited] = React.useState(false);

  const startTimer = () => {
    if (open) {
      timeoutRef.current = setTimeout(() => {
        setOpen(false);
        if (onClose) {
          onClose();
        }
      }, duration);
    }
  };

  const stopTimer = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const restartTimer = () => {
    if (!timeoutRef.current) {
      startTimer();
    }
  };

  const handleClose = (event) => {
    setExited(true);
    squarePosition.current = ({ x: 0, y: 0 });
  };

  const handleDrag = (data) => {
    if (data?.movement?.my > 0) {
      containerRef.current.style.transform = `translate3d(0, ${data?.movement?.my * 1.05}px, 0)`;
      squarePosition.current = ({ x: 0, y: data?.movement?.my * 1.05 });
      if (data?.movement?.my > 200) {
        setExited(true);
      }
    }
  };

  const handleExited = (event) => {
    setExited(false);
    setOpen(false);

    if (onClose) {
      onClose(event);
    }
  };

  const handleBlur = () => {
    restartTimer();
  };

  const handleFocus = () => {
    stopTimer();
  };

  const handleKeyDown = React.useCallback((event) => {
    if (event.key === 'Escape') {
      setExited(true);
    }

    if (event.key === 'Tab' && !event.shiftKey) {
      const previouslyFocusedElement = document.activeElement;
      const isCloseButtonFocused = closeButtonRef?.current === previouslyFocusedElement;
      const isLinkFocused = linkRef?.current === previouslyFocusedElement;

      if (!isCloseButtonFocused && !isLinkFocused) {
        event.preventDefault();
        if (closeButtonRef?.current) {
          closeButtonRef?.current?.focus();
        } else if (linkRef?.current) {
          linkRef?.current?.focus();
        }
      } else if (triggerEl) {
        event.preventDefault();
        if (triggerEl?.focus) {
          triggerEl.focus();
        }
      }
    }

    if (onKeyDown) {
      onKeyDown(event);
    }
  }, [onKeyDown, setExited, triggerEl]);

  const handleMouseEnter = () => {
    stopTimer();
  };

  const handleMouseLeave = () => {
    restartTimer();
  };

  const handleSwipe = (data) => {
    squarePosition.current = ({ x: 0, y: data?.swipeY * 100 + data?.movement?.my });
    setExited(true);
  };

  const closeNode = onClose && (
    <IconButton
      color="inverse"
      icon={Close}
      iconSize="small"
      onBlur={handleBlur}
      onClick={handleClose}
      onFocus={handleFocus}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      ref={closeButtonRef}
    />
  );

  const isLink = React.isValidElement(linkProp) && (
    linkProp.type === Link || linkProp.type?.displayName === 'Link'
  );

  const linkNode = isLink && React.cloneElement(linkProp, {
    className: 'sui-focus-outline-inverse sui-rounded-md',
    color: 'inverse',
    height: 'tight',
    onClick: () => {
      handleClose();
      if (linkProp?.props?.onClick) {
        linkProp?.props?.onClick();
      }
    },
    onBlur: handleBlur,
    onFocus: handleFocus,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    ref: linkRef,
    underline: 'always',
    variant: 'body-sm',
    weight: 'bold'
  });

  const action = linkNode || closeNode;

  const isImage = decorationProp?.type === 'img';
  const decoration = React.isValidElement(decorationProp) && (
    isImage
      ? React.cloneElement(decorationProp, { className: 'sui-w-7 sui-h-7 sui-rounded-md', width: 28, height: 28, style: { objectFit: 'contain' } })
      : React.isValidElement(decorationProp) && React.cloneElement(decorationProp, { size: 'small', color: 'inverse' })
  );

  const isFragment = (child) => React.isValidElement(child) && child.type === React.Fragment;

  const isTypography = (child) => React.isValidElement(child) && (
    child.type === Typography || child.type?.displayName === 'Typography'
  );

  const message = typeof messageProp === 'string'
    ? (
      <Typography variant="body-sm" height="tight" color="inverse" truncate={messageProp?.props?.children?.length > 1}>
        { messageProp }
      </Typography>
    )
    : (
      React.Children.map(messageProp, (child) => {
        if (!isFragment(child)) return;

        return React.Children.map(child?.props?.children, (nestedChild) => {
          if (!isTypography(nestedChild)) return;

          return React.cloneElement(nestedChild, {
            variant: 'body-sm',
            height: 'tight',
            color: 'inverse',
            truncate: true,
          });
        });
      })
    );

  const classes = {
    root: classNames('sui-flex sui-justify-center sui-bottom-0 sui-w-screen sui-min-h-11 sui-z-toast sui-pb-4 sui-px-2 sm:sui-px-0 md:sui-pb-8 lg:sui-pb-8 xl:sui-pb-10', {
      'sui-fixed': !disablePortal,
      'sui-absolute': disablePortal
    }),
    container: classNames('sui-min-w-96 sui-w-96 sui-h-full sui-flex sui-bg-inverse sui-rounded-lg sui-shadow-md'),
    link: classNames('sui-flex sui-flex-shrink-0 sui-items-center sui-justify-center sui-h-11', {
      'sui-px-4': linkNode,
      'sui-w-11': !linkNode
    }),
    decoration: classNames('sui-flex sui-flex-shrink-0 sui-items-center sui-justify-center sui-mx-2 sui-w-7 sui-h-11'),
    text: classNames('sui-flex sui-grow sui-py-3', {
      'sui-flex-col sui-justify-center sui-min-w-0': messageProp?.props?.children?.length > 1,
      'sui-pr-2': action,
      'sui-pr-4': !action,
      'sui-pl-4': !decoration,
    })
  };

  React.useEffect(() => {
    startTimer();
    return () => stopTimer();
  });

  React.useEffect(() => {
    if (!open) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, handleKeyDown]);

  if (!open) return null;

  return (
    <Portal container={anchorEl} disablePortal={disablePortal}>
      <div className={classes.root} ref={ref} {...restProps}>
        <TransitionFade
          in={!exited}
          timeout={300}
          transitionStyles={{
            entering: {
              opacity: 0,
              transform: 'translate3d(0, 58px, 0)',
              transitionProperty: 'transform',
              transitionDuration: '500ms',
              transitionTimingFunction: 'ease-in'
            },
            entered: {
              opacity: 1,
              transform: 'translate3d(0, 0, 0)',
              transitionProperty: 'transform',
              transitionDuration: '500ms',
              transitionTimingFunction: 'ease-in'
            },
            exiting: {
              opacity: 1,
              transform: `scale(0.8) translate3d(0, ${squarePosition.current.y}px, 0)`,
              transitionProperty: 'transform, opacity',
              transitionDuration: '500ms',
              transitionTimingFunction: 'ease-out'
            },
            exited: {
              opacity: 0,
              transform: `scale(0.1) translate3d(0, ${squarePosition.current.y}px, 0)`,
              transitionProperty: 'transform, opacity',
              transitionDuration: '500ms',
              transitionTimingFunction: 'ease-out'
            }
          }}
          onExited={handleExited}
        >
          <div>
            <Drag on={open} lockAxis="vertical" onSwipe={handleSwipe} onDrag={handleDrag}>
              <div aria-live="polite" ref={containerRef} className={classes.container} role="status">
                {
                  decoration && (
                    <div className={classes.decoration}>
                      { decoration }
                    </div>
                  )
                }
                <div className={classes.text}>
                  { message }
                </div>
                {
                  action && (
                    <div className={classes.link}>
                      { action }
                    </div>
                  )
                }
              </div>
            </Drag>
          </div>
        </TransitionFade>
      </div>
    </Portal>
  );
});

Toast.displayName = 'Toast';

Toast.propTypes = {
  /**
   * The HTML element or function that returns an element to anchor the toast to.
   * Currently not implemented in this component.
   * @default document.body
   */
  anchorEl: PropTypes.oneOfType([HTMLElementType, PropTypes.func]),
  /**
   * Decoration component or element to display in the toast notification.
   */
  decoration: PropTypes.node,
  /**
   * If `true`, the portal functionality is disabled and the toast is rendered directly in place.
   * When disabled, the toast will not be rendered in a portal container.
   * @default false
   * @ignore
   */
  disablePortal: PropTypes.bool,
  /**
   * The duration in milliseconds to display the toast notification before auto-dismissing.
   * @default 5000
   */
  duration: PropTypes.oneOf([5000, 7000, 9000]),
  /**
   * Link component to display in the toast notification.
   */
  link: PropTypes.node,
  /**
   * The message text to display in the toast notification.
   */
  message: PropTypes.node.isRequired,
  /**
   * Callback function to be called when the toast notification is closed.
   */
  onClose: PropTypes.func,
  /**
   * Callback function to be called when the toast notification is closed.
   */
  onKeyDown: PropTypes.func,
  /**
   * If `true`, the toast notification is visible.
   * @default false
   */
  open: PropTypes.bool
};

export { Toast };
